<script lang="ts" setup>
import { iconMinus, iconPlus } from "@sit-onyx/icons";
import { computed, ref, useTemplateRef, watchEffect } from "vue";
import { useDensity } from "../../composables/density.js";
import { useAutofocus } from "../../composables/useAutoFocus.js";
import { useErrorClass } from "../../composables/useErrorClass.js";
import { getFormMessages, useFormElementError } from "../../composables/useFormElementError.js";
import {
  SKELETON_INJECTED_SYMBOL,
  useSkeletonContext,
} from "../../composables/useSkeletonState.js";
import { useVModel } from "../../composables/useVModel.js";
import { injectI18n } from "../../i18n/index.js";
import type { Nullable } from "../../types/index.js";
import { useRootAttrs } from "../../utils/attrs.js";
import { applyLimits, roundToPrecision } from "../../utils/numbers.js";
import { useForwardProps } from "../../utils/props.js";
import { FORM_INJECTED_SYMBOL, useFormContext } from "../OnyxForm/OnyxForm.core.js";
import OnyxFormElement from "../OnyxFormElement/OnyxFormElement.vue";
import OnyxIcon from "../OnyxIcon/OnyxIcon.vue";
import OnyxLoadingIndicator from "../OnyxLoadingIndicator/OnyxLoadingIndicator.vue";
import OnyxSkeleton from "../OnyxSkeleton/OnyxSkeleton.vue";
import type { OnyxStepperProps } from "./types.js";

const props = withDefaults(defineProps<OnyxStepperProps>(), {
  stepSize: 1,
  readonly: false,
  loading: false,
  skeleton: SKELETON_INJECTED_SYMBOL,
  disabled: FORM_INJECTED_SYMBOL,
  showError: FORM_INJECTED_SYMBOL,
  requiredMarker: FORM_INJECTED_SYMBOL,
  reserveMessageSpace: FORM_INJECTED_SYMBOL,
  hideButtons: false,
});

const emit = defineEmits<{
  /**
   * Emitted when the validity state of the input changes.
   */
  validityChange: [validity: ValidityState];
  /**
   * update modeValue
   */
  "update:modelValue": [value?: Nullable<number>];
}>();

const { t, locale } = injectI18n();
const input = useTemplateRef("input");

const { disabled, showError } = useFormContext(props);
const skeleton = useSkeletonContext(props);
const errorClass = useErrorClass(showError);
const { densityClass } = useDensity(props);
const { vCustomValidity, errorMessages } = useFormElementError({ props, emit });
const successMessages = computed(() => getFormMessages(props.success));
const messages = computed(() => getFormMessages(props.message));
const formElementProps = useForwardProps(props, OnyxFormElement);

defineOptions({ inheritAttrs: false });
const { rootAttrs, restAttrs } = useRootAttrs();

/**
 * Used to detect user interaction to simulate the behavior of :user-invalid for the native input
 * because the native browser :user-invalid does not trigger when the value is changed via Arrow up/down or increase/decrease buttons
 */
const wasTouched = ref(false);

const modelValue = useVModel({
  props,
  emit,
  key: "modelValue",
});

/**
 * Used for syncing the actual input value.
 * We use string to be able to control the number of decimal places.
 */
const inputValue = ref<string>();
const displayValue = computed(() => getDisplayValue.value(modelValue.value));
const getFormattedValue = computed(() => {
  return (value?: Nullable<number>) => {
    if (value != undefined && isNaN(value)) return "";

    // using "!=" here to check for both undefined and null
    if (props.precision !== undefined && value != undefined) {
      return roundToPrecision(value, props.precision);
    } else {
      return value?.toString() ?? "";
    }
  };
});
const getDisplayValue = computed(() => {
  return (value: Nullable<number>) => {
    if (value == undefined || Number.isNaN(value)) return "";
    if (props.formatNumber) {
      if (typeof props.formatNumber === "boolean") {
        if (props.precision) {
          return value.toLocaleString(locale.value, {
            minimumFractionDigits: props.precision,
            maximumFractionDigits: props.precision,
          });
        }
        return value.toLocaleString(locale.value);
      } else {
        return props.formatNumber(value);
      }
    }
    if (props.precision) {
      return roundToPrecision(value, props.precision);
    }
    return String(value);
  };
});

watchEffect(() => {
  inputValue.value = getFormattedValue.value(modelValue.value);
});

const handleClick = (direction: "stepUp" | "stepDown") => {
  wasTouched.value = true;
  const currentValue = modelValue.value || 0;
  const stepValue = (direction === "stepUp" ? 1 : -1) * props.stepSize;
  const newValue = parseFloat(getFormattedValue.value(currentValue + stepValue));
  modelValue.value = applyLimits(newValue, props.min, props.max);
};

const handleChange = () => {
  wasTouched.value = true;
  if (inputValue.value == undefined) {
    modelValue.value = undefined;
    return;
  }
  inputValue.value = getFormattedValue.value(parseFloat(inputValue.value));
  const parsedValue = parseFloat(inputValue.value);
  modelValue.value = isNaN(parsedValue) ? undefined : parsedValue;
};

const handleBlur = () => {
  // Reformat the value on blur
  inputValue.value = getFormattedValue.value(modelValue.value);
};

const incrementLabel = computed(() => t.value("stepper.increment", { stepSize: props.stepSize }));
const decrementLabel = computed(() => t.value("stepper.decrement", { stepSize: props.stepSize }));

defineExpose({ input });
useAutofocus(input, props);
</script>

<template>
  <div
    v-if="skeleton"
    :class="['onyx-component', 'onyx-stepper-skeleton', densityClass]"
    v-bind="rootAttrs"
  >
    <OnyxSkeleton v-if="!props.hideLabel" class="onyx-stepper-skeleton__label" />
    <OnyxSkeleton class="onyx-stepper-skeleton__input" />
  </div>
  <div
    v-else
    :class="['onyx-component', 'onyx-stepper', densityClass, errorClass]"
    v-bind="rootAttrs"
  >
    <OnyxFormElement
      v-bind="formElementProps"
      :message="messages"
      :success-messages="successMessages"
      :error-messages="errorMessages"
    >
      <template #default="{ id: inputId }">
        <div class="onyx-stepper__wrapper">
          <button
            v-if="!props.hideButtons"
            type="button"
            class="onyx-stepper__counter"
            :disabled="
              disabled ||
              readonly ||
              props.loading ||
              (props.min !== undefined &&
                modelValue !== undefined &&
                modelValue !== null &&
                modelValue <= props.min)
            "
            :aria-label="decrementLabel"
            tabindex="-1"
            @click="handleClick('stepDown')"
          >
            <OnyxIcon :icon="iconMinus" />
          </button>
          <OnyxLoadingIndicator v-if="props.loading" class="onyx-stepper__loading" type="circle" />
          <input
            v-else
            :id="inputId"
            ref="input"
            v-model="inputValue"
            v-custom-validity
            class="onyx-stepper__native"
            :class="{ 'onyx-stepper__native--touched': wasTouched }"
            type="number"
            :aria-label="props.label"
            :autofocus="props.autofocus"
            :disabled="disabled || props.loading"
            :min="props.min"
            :max="props.max"
            :name="props.name"
            :placeholder="props.placeholder"
            :readonly="props.readonly"
            :required="props.required"
            :step="props.validStepSize ?? 'any'"
            :title="props.hideLabel ? props.label : undefined"
            v-bind="restAttrs"
            @change="handleChange"
            @blur="handleBlur"
            @focusin="(e) => (e.target as HTMLInputElement)?.select()"
            @keydown.up.prevent="handleClick('stepUp')"
            @keydown.down.prevent="handleClick('stepDown')"
          />
          <p class="onyx-stepper__display" aria-hidden="true">
            {{ displayValue }}
          </p>
          <button
            v-if="!props.hideButtons"
            type="button"
            class="onyx-stepper__counter"
            :disabled="
              disabled ||
              readonly ||
              props.loading ||
              (props.max !== undefined &&
                modelValue !== undefined &&
                modelValue !== null &&
                modelValue >= props.max)
            "
            :aria-label="incrementLabel"
            tabindex="-1"
            @click="handleClick('stepUp')"
          >
            <OnyxIcon :icon="iconPlus" />
          </button>
        </div>
      </template>
    </OnyxFormElement>
  </div>
</template>

<style lang="scss">
@use "../../styles/mixins/layers";
@use "../../styles/mixins/input.scss";

.onyx-stepper,
.onyx-stepper-skeleton {
  @include layers.component() {
    --onyx-stepper-padding-vertical: var(--onyx-density-xs);
  }
}

.onyx-stepper-skeleton {
  @include layers.component() {
    @include input.define-skeleton-styles(
      $height: calc(1lh + 2 * var(--onyx-stepper-padding-vertical))
    );
  }
}

.onyx-stepper {
  @include layers.component() {
    @include input.define-shared-styles(
      $base-selector: ".onyx-stepper",
      $vertical-padding: var(--onyx-stepper-padding-vertical)
    );

    &__wrapper {
      gap: 0;
      padding: 0;
      justify-content: space-between;
      position: relative;

      &:has(.onyx-stepper__native:focus-visible) {
        .onyx-stepper__display {
          display: none;
        }
      }
    }

    &__counter {
      border: none;
      height: 100%;
      background-color: transparent;
      color: var(--onyx-color-text-icons-neutral-medium);
      display: inline-flex;
      align-items: center;
      padding: var(--onyx-stepper-padding-vertical);
      border-radius: inherit;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      outline: none;

      &:enabled {
        cursor: pointer;

        &:hover,
        &:focus-visible {
          color: var(--onyx-color-text-icons-primary-intense);
        }

        &:focus-visible {
          outline: none;
          background-color: var(--onyx-color-base-primary-100);
        }

        &:active {
          background-color: var(--onyx-color-base-primary-100);
        }

        &:first-child {
          border-top-left-radius: inherit;
          border-bottom-left-radius: inherit;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      }

      &:disabled {
        color: var(--onyx-color-text-icons-neutral-soft);
      }
    }

    &__native {
      -moz-appearance: textfield;
      text-align: center;
      &:not(:focus-visible) {
        color: transparent;
      }

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }

    &__display {
      position: absolute;
      height: 100%;
      color: var(--onyx-color-text-icons-neutral-intense);

      pointer-events: none;
      display: flex;
      justify-content: center;
      align-items: center;
      box-sizing: border-box;
      overflow: hidden;
      white-space: nowrap;
      width: 100%;
    }

    &:has(&__counter) {
      .onyx-stepper__display {
        $button-width: calc(2 * var(--onyx-stepper-padding-vertical) + 1.5rem);
        width: calc(100% - 2 * $button-width);
        margin-inline: $button-width;
      }
    }
  }
}
</style>
